import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { validatePassword } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { BlurView } from 'expo-blur';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('RESET_PASSWORD');

export function ResetPasswordScreen() {
  const { resetPassword } = useAuth();
  const params = useLocalSearchParams<{ token?: string }>();

  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (params.token) {
      setToken(params.token);
      return;
    }

    if (process.env.EXPO_OS === 'web' && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromUrl = urlParams.get('token');
      if (tokenFromUrl) {
        setToken(tokenFromUrl);
        return;
      }
    }

    logger.warn('No reset token found in URL or params');
    toast.error('This password reset link is invalid or has expired. Please request a new one.');
    router.replace({ pathname: '/(auth)/login' });
  }, [params.token]);

  const validateInputs = () => {
    const newErrors: Record<string, string> = {};

    if (!token) newErrors.token = 'Reset token is required';

    const passwordError = validatePassword(newPassword);
    if (passwordError) newErrors.newPassword = passwordError;

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validateInputs()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const result = await resetPassword(token, newPassword);

      if (result.error) {
        logger.error('Reset password failed:', result.error);
        const errorMessage = result.error.message || 'Failed to reset password. Please try again.';

        if (errorMessage.toLowerCase().includes('token') ||
            errorMessage.toLowerCase().includes('expired') ||
            errorMessage.toLowerCase().includes('invalid')) {
          toast.error('This password reset link is invalid or has expired. Please request a new one.');
          router.replace({ pathname: '/(auth)/forgot-password' });
        } else {
          setErrors({ newPassword: errorMessage });
        }
      } else {
        logger.log('Password reset successful');
        setIsSuccess(true);
        setTimeout(() => router.replace({ pathname: '/(auth)/login' }), 2000);
      }
    } catch {
      setErrors({ newPassword: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!token && !isSuccess) {
    return (
      <View className="flex-1">
        <SafeAreaView className="flex-1">
          <View className="flex-1 justify-center items-center">
            <Text className="text-warm-gray-100">Loading...</Text>
          </View>
        </SafeAreaView>
      </View>
    );
  }

  return (
    <AuthScreenLayout title="Reset Password">
      <BlurView intensity={50} className="rounded-xl overflow-hidden p-6" style={{ width: '100%' }}>
        {isSuccess ? (
          <View className="space-y-4">
            <Text className="text-xl font-semibold text-center mb-2 text-warm-gray-100">
              Password Reset Successful!
            </Text>
            <Text className="text-base text-center text-warm-gray-100">
              Your password has been reset successfully. Redirecting to login...
            </Text>
          </View>
        ) : (
          <View className="space-y-4">
            <Text className="text-xl font-semibold text-center mb-2 text-warm-gray-100">
              Reset Your Password
            </Text>
            <Text className="text-base text-center mb-4 text-warm-gray-100">
              Enter your new password below.
            </Text>

            <TextInput
              placeholder="New Password"
              value={newPassword}
              onChangeText={(text) => {
                setNewPassword(text);
                if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: '' }));
              }}
              secureTextEntry={!showPassword}
              editable={!isLoading}
              error={errors.newPassword || undefined}
              containerClassName="my-1"
              rightIcon={
                <Pressable onPress={() => setShowPassword(!showPassword)}>
                  <Text className="text-foreground">{showPassword ? 'Hide' : 'Show'}</Text>
                </Pressable>
              }
            />

            <TextInput
              placeholder="Confirm Password"
              value={confirmPassword}
              onChangeText={(text) => {
                setConfirmPassword(text);
                if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
              }}
              secureTextEntry={!showConfirmPassword}
              editable={!isLoading}
              error={errors.confirmPassword || undefined}
              containerClassName="my-1"
              rightIcon={
                <Pressable onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                  <Text className="text-foreground">{showConfirmPassword ? 'Hide' : 'Show'}</Text>
                </Pressable>
              }
            />

            <Pressable
              className="p-4 my-4 rounded-lg bg-primary"
              onPress={handleResetPassword}
              disabled={isLoading}
            >
              <Text className="font-semibold text-center text-warm-gray-950">
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Text>
            </Pressable>
          </View>
        )}
      </BlurView>

      {!isSuccess && (
        <View className="flex-row justify-center mt-6">
          <Pressable onPress={() => router.replace({ pathname: '/(auth)/login' })}>
            <Text className="font-semibold underline text-warm-gray-100">Back to Login</Text>
          </Pressable>
        </View>
      )}
    </AuthScreenLayout>
  );
}
